import React, { useState, useEffect } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Settings, Crown, LogOut, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import DeleteTeamDialog from "./DeleteTeamDialog";
import TransferLeadershipDialog from "./TransferLeadershipDialog";

interface TeamMember {
  id: string;
  user_id: string;
  email: string;
  role: string;
}

interface TeamManagementDropdownProps {
  teamId: string;
  teamName: string;
  currentUserId: string;
  challengeId: string;
  leaderName: string;
  onUpdate: () => void;
  onTeamDeleted: () => void;
}

const TeamManagementDropdown: React.FC<TeamManagementDropdownProps> = ({
  teamId,
  teamName,
  currentUserId,
  challengeId,
  leaderName,
  onUpdate,
  onTeamDeleted,
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        // Fetch team members
        const { data: membersData, error: membersError } = await supabase
          .from("challenge_team_members")
          .select("id, user_id, role")
          .eq("team_id", teamId);

        if (membersError) throw membersError;

        // Fetch emails from profiles
        const userIds = membersData?.map((m) => m.user_id) || [];
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, email")
          .in("id", userIds);

        const profileMap = new Map(profiles?.map((p) => [p.id, p.email || ""]));

        const enrichedMembers: TeamMember[] = (membersData || []).map((m) => ({
          id: m.id,
          user_id: m.user_id,
          email: profileMap.get(m.user_id) || "Unknown",
          role: m.role || "member",
        }));

        setMembers(enrichedMembers);
      } catch (err) {
        console.error("Failed to fetch team members:", err);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchMembers();
  }, [teamId]);

  const otherMembers = members.filter((m) => m.user_id !== currentUserId);
  const hasOtherMembers = otherMembers.length > 0;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Settings className="h-4 w-4" />
            Manage Team
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-popover">
          <DropdownMenuItem
            onClick={() => setShowTransferDialog(true)}
            disabled={!hasOtherMembers || loadingMembers}
            className="gap-2"
          >
            <Crown className="h-4 w-4" />
            Transfer Leadership
            {!hasOtherMembers && !loadingMembers && (
              <span className="text-xs text-muted-foreground ml-auto">No other members</span>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setShowLeaveDialog(true)}
            disabled={!hasOtherMembers || loadingMembers}
            className="gap-2"
          >
            <LogOut className="h-4 w-4" />
            Leave Team
            {!hasOtherMembers && !loadingMembers && (
              <span className="text-xs text-muted-foreground ml-auto">No other members</span>
            )}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setShowDeleteDialog(true)}
            className="gap-2 text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Delete Team
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteTeamDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        teamId={teamId}
        teamName={teamName}
        memberCount={members.length}
        challengeId={challengeId}
        leaderName={leaderName}
        onTeamDeleted={onTeamDeleted}
      />

      <TransferLeadershipDialog
        open={showTransferDialog}
        onOpenChange={setShowTransferDialog}
        teamId={teamId}
        teamName={teamName}
        members={members}
        currentUserId={currentUserId}
        onTransferComplete={onUpdate}
        leaveAfterTransfer={false}
      />

      <TransferLeadershipDialog
        open={showLeaveDialog}
        onOpenChange={setShowLeaveDialog}
        teamId={teamId}
        teamName={teamName}
        members={members}
        currentUserId={currentUserId}
        onTransferComplete={onTeamDeleted}
        leaveAfterTransfer={true}
      />
    </>
  );
};

export default TeamManagementDropdown;
